import ReactMde, { getDefaultCommandMap } from 'react-mde';
import { FormattedMessage, useIntl } from 'react-intl';
import { Theme } from '@mui/material';
import { converter } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useMemo, useState } from 'react';

const useStyles = makeStyles<Theme, GrowBounds, 'root' | 'readOnly' | 'preview' | 'error' | 'grow'>((theme: Theme) => ({
    root: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        fontFamily: 'Inter',
        color: theme.palette.text.primary,
        '& svg': {
            color: theme.palette.text.secondary,
            '&:hover': {
                color: theme.palette.text.primary,
            },
        },
    },
    readOnly: {
        backgroundColor: theme.palette.background.default,
        // Locked, not faded: half-transparent text was hard to read (R-261).
        color: theme.palette.text.secondary,
        '& .mde-header': {
            display: 'none',
        },
    },
    preview: {
        padding: '20px 32px',
        '& > div': {
            padding: '0 !important',
        },
    },
    error: {
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.error.main}`,
    },
    // react-mde gives the textarea a fixed height; this lets it follow the text
    // between the two bounds instead. Browsers without field-sizing keep the
    // minimum and scroll.
    grow: {
        '& textarea.mde-text': {
            fieldSizing: 'content',
            height: 'auto !important',
            minHeight: ({ minHeight }: GrowBounds) => minHeight,
            maxHeight: ({ maxHeight }: GrowBounds) => maxHeight,
            overflowY: 'auto',
        },
    },
}));

interface GrowBounds {
    minHeight: number;
    maxHeight?: number;
}

export interface PoolMarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    error?: boolean;
    minHeight?: number;
    /** Set it and the editor grows with its text from `minHeight` up to this, then scrolls. */
    maxHeight?: number;
    name?: string;
}

/** The buttons this editor shows, and what each one is called. */
const TOOLBAR: Record<string, string> = {
    bold: 'app.common.editor.bold',
    italic: 'app.common.editor.italic',
    strikethrough: 'app.common.editor.strikethrough',
    link: 'app.common.editor.link',
    'unordered-list': 'app.common.editor.unorderedList',
    'ordered-list': 'app.common.editor.orderedList',
};

export const PoolMarkdownEditor = (props: PoolMarkdownEditorProps) => {
    const { readOnly, error, minHeight = 210, maxHeight, onChange, name, ...markdownEditorProps } = props;
    const classes = useStyles({ minHeight, maxHeight });
    const intl = useIntl();
    const [selectedTab, setSelectedTab] = useState<'write' | 'preview'>('write');

    // The library ships its buttons with English labels ("Add bold text"),
    // which is what a screen reader reads out and what the tooltip shows. The
    // commands themselves are kept, only what they are called is replaced
    // (#2212).
    const commands = useMemo(() => {
        const defaults = getDefaultCommandMap();

        return Object.entries(TOOLBAR).reduce((all, [command, id]) => {
            const label = intl.formatMessage({ id });

            return {
                ...all,
                [command]: {
                    ...defaults[command],
                    buttonProps: { ...defaults[command]?.buttonProps, 'aria-label': label, title: label },
                },
            };
        }, defaults);
    }, [intl]);

    return (
        <ReactMde
            {...markdownEditorProps}
            commands={commands}
            onChange={onChange}
            selectedTab={readOnly ? 'preview' : selectedTab}
            onTabChange={tab => setSelectedTab(tab)}
            generateMarkdownPreview={markdown => Promise.resolve(converter.makeHtml(markdown))}
            toolbarCommands={[
                ['bold', 'italic', 'strikethrough'],
                ['link', 'unordered-list', 'ordered-list'],
            ]}
            classes={{
                reactMde: `${classes.root} ${readOnly && classes.readOnly} ${error && classes.error} ${
                    maxHeight && classes.grow
                }`,
                preview: readOnly ? classes.preview : '',
            }}
            minEditorHeight={minHeight}
            minPreviewHeight={maxHeight ? minHeight : undefined}
            l18n={{
                write: <FormattedMessage id="app.common.edit" />,
                preview: <FormattedMessage id="app.common.preview" />,
                pasteDropSelect: '',
                uploadingImage: '',
            }}
            childProps={{
                textArea: {
                    name,
                },
            }}
        />
    );
};
